import ReactECharts from 'echarts-for-react';
import { Icon } from '@iconify/react';
import {
  useTheme,
  AnalystDelta,
  AnalystMetricConfig,
  AnalystMetricTile,
  CHART_TOKENS,
  formatValue,
} from '@circuitry-ai/doc-ui';

function DeltaChip({ delta }: { readonly delta?: AnalystDelta }) {
  if (!delta) return null;
  const up = delta.direction !== 'down';
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
        up ? 'bg-success/10 text-success' : 'bg-[#FCE7F3] text-[#D4007F]'
      }`}
    >
      <Icon icon={up ? 'mdi:arrow-up' : 'mdi:arrow-down'} width={12} />
      {delta.value}%
    </span>
  );
}

function Sparkline({ points, up }: { readonly points: number[]; readonly up: boolean }) {
  const { isDark } = useTheme();
  // Sparkline lines are SVG-rendered ECharts (inline colours CSS can't reach),
  // so pick the calm muted palette directly in dark mode.
  const lightColor = up ? CHART_TOKENS['chart-primary'] : CHART_TOKENS['chart-highlight'];
  const darkColor = up ? '#56b6c2' : '#e57ba3';
  const color = isDark ? darkColor : lightColor;
  const option = {
    grid: { left: 0, right: 0, top: 2, bottom: 2 },
    xAxis: { type: 'category', show: false, data: points.map((_, index) => index) },
    yAxis: { type: 'value', show: false, scale: true },
    series: [
      {
        type: 'line',
        data: points,
        smooth: true,
        symbol: 'none',
        lineStyle: { color, width: 2 },
        areaStyle: { color, opacity: 0.12 },
      },
    ],
  };
  return <ReactECharts option={option} style={{ height: 28, width: 72 }} opts={{ renderer: 'svg' }} />;
}

/**
 * A single metric tile. `hero` renders the large centered KPI card used when
 * `tiles.length === 1`; otherwise it is a tile-band cell.
 */
function MetricTile({ tile, hero = false }: { readonly tile: AnalystMetricTile; readonly hero?: boolean }) {
  const up = tile.delta?.direction !== 'down';
  const points = tile.sparkline?.points;
  const hasSpark = Array.isArray(points) && points.length >= 2;
  return (
    <div className={`flex flex-col gap-1 rounded-lg border border-default-200 bg-content1 p-3 ${hero ? 'items-center text-center' : ''}`}>
      <span className="text-[13px] font-normal text-default-500" style={{ fontFamily: 'Poppins, sans-serif' }}>
        {tile.label}
      </span>
      <span
        className="leading-none text-primary"
        style={{ fontFamily: 'Oswald, sans-serif', fontSize: hero ? 44 : 28, fontWeight: 700 }}
      >
        {formatValue(tile.value, tile.format)}
      </span>
      {(tile.delta || hasSpark) && (
        <div className={`mt-1 flex items-center gap-2 ${hero ? 'justify-center' : 'justify-between'}`}>
          <div className="flex flex-col">
            <DeltaChip delta={tile.delta} />
            {tile.delta?.label ? <span className="text-[10px] text-default-400">{tile.delta.label}</span> : null}
          </div>
          {hasSpark ? <Sparkline points={points} up={up} /> : null}
        </div>
      )}
    </div>
  );
}

/**
 * Metric block — always renders `config.tiles[]`. Layout is inferred from the
 * tile count: a single tile becomes a hero KPI card; two or more become a
 * responsive tile band (contract §6).
 */
export function AnalystMetrics({ config }: { readonly config: AnalystMetricConfig }) {
  const tiles = config.tiles ?? [];
  const hero = tiles.length === 1;
  return (
    <div>
      {config.title ? (
        <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-default-500">{config.title}</div>
      ) : null}
      {hero ? (
        <MetricTile tile={tiles[0]} hero />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
          {tiles.map((tile) => (
            <MetricTile key={tile.label} tile={tile} />
          ))}
        </div>
      )}
    </div>
  );
}

export default AnalystMetrics;
